import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "../../../api";

// Centralised query keys keep cache invalidation precise.
export const crKeys = {
    all: ["contractReview"],
    summary: () => [...crKeys.all, "summary"],
    list: (tab) => [...crKeys.all, "list", tab],
    pendingCreation: () => [...crKeys.all, "pendingCreation"],
    detail: (id) => [...crKeys.all, "detail", id],
    requirements: (dev) => [...crKeys.all, "lookups", "requirements", dev],
    employees: () => [...crKeys.all, "lookups", "employees"],
    salesReps: () => [...crKeys.all, "lookups", "salesReps"],
    customers: (q) => [...crKeys.all, "lookups", "customers", q],
    history: (custId, exclude, sswPart) => [...crKeys.all, "lookups", "history", custId, exclude, sswPart],
};

// ===================================================================================
//  Reads
// ===================================================================================
export function useCrSummary() {
    return useQuery({
        queryKey: crKeys.summary(),
        queryFn: async () => (await api.get("ContractReview/summary")).data,
        staleTime: 30_000,
    });
}

export function useCrList(tab, enabled = true) {
    return useQuery({
        queryKey: crKeys.list(tab),
        queryFn: async () => (await api.get(`ContractReview/list?tab=${encodeURIComponent(tab)}`)).data,
        enabled,
        staleTime: 30_000,
    });
}

export function useCrPendingCreation(enabled = true) {
    return useQuery({
        queryKey: crKeys.pendingCreation(),
        queryFn: async () => (await api.get("ContractReview/pendingCreation")).data,
        enabled,
        staleTime: 30_000,
    });
}

export function useCrDetail(id, enabled = true) {
    return useQuery({
        queryKey: crKeys.detail(id),
        queryFn: async () => (await api.get(`ContractReview/${id}`)).data,
        enabled: enabled && !!id && id !== "new",
        staleTime: 10_000,
    });
}

export function useCrRequirements(developmental) {
    return useQuery({
        queryKey: crKeys.requirements(developmental),
        queryFn: async () =>
            (await api.get(`ContractReview/lookups/requirements?dev=${developmental ? "true" : "false"}`)).data,
        staleTime: 5 * 60_000,
    });
}

export function useCrEmployees() {
    return useQuery({
        queryKey: crKeys.employees(),
        queryFn: async () => (await api.get("ContractReview/lookups/employees")).data,
        staleTime: 5 * 60_000,
    });
}

export function useCrSalesReps() {
    return useQuery({
        queryKey: crKeys.salesReps(),
        queryFn: async () => (await api.get("ContractReview/lookups/salesreps")).data,
        staleTime: 5 * 60_000,
    });
}

export function useCrCustomers(q, enabled = true) {
    return useQuery({
        queryKey: crKeys.customers(q),
        queryFn: async () =>
            (await api.get(`ContractReview/lookups/customers?q=${encodeURIComponent(q || "")}&take=200`)).data,
        enabled,
        staleTime: 60_000,
    });
}

export function useCrHistory(custId, excludeCrId, sswPart, enabled = true) {
    return useQuery({
        queryKey: crKeys.history(custId, excludeCrId, sswPart),
        queryFn: async () => {
            const params = new URLSearchParams({
                custId: custId || "",
                excludeCrId: String(excludeCrId || 0),
            });
            if (sswPart) params.append("sswPart", sswPart);
            return (await api.get(`ContractReview/lookups/history?${params.toString()}`)).data;
        },
        enabled: enabled && !!custId,
    });
}

// ===================================================================================
//  Writes
// ===================================================================================
export function useCrSave() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async ({ id, payload }) => {
            if (id && id !== "new") return (await api.put(`ContractReview/${id}`, payload)).data;
            return (await api.post("ContractReview", payload)).data;
        },
        onSuccess: (_data, vars) => {
            qc.invalidateQueries({ queryKey: crKeys.all });
            if (vars?.id) qc.invalidateQueries({ queryKey: crKeys.detail(vars.id) });
        },
    });
}

export function useCrSubmit() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (id) => (await api.post(`ContractReview/${id}/submit`)).data,
        onSuccess: (_d, id) => {
            qc.invalidateQueries({ queryKey: crKeys.all });
            qc.invalidateQueries({ queryKey: crKeys.detail(id) });
        },
    });
}

export function useCrCancel() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (id) => (await api.post(`ContractReview/${id}/cancel`)).data,
        onSuccess: () => qc.invalidateQueries({ queryKey: crKeys.all }),
    });
}

export function useCrArchive() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (id) => (await api.post(`ContractReview/${id}/archive`)).data,
        onSuccess: () => qc.invalidateQueries({ queryKey: crKeys.all }),
    });
}

export function useCrWonLost() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async ({ id, payload }) => (await api.post(`ContractReview/${id}/wonlost`, payload)).data,
        onSuccess: () => qc.invalidateQueries({ queryKey: crKeys.all }),
    });
}

export function useCrDelete() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (id) => (await api.delete(`ContractReview/${id}`)).data,
        onSuccess: () => qc.invalidateQueries({ queryKey: crKeys.all }),
    });
}

export function useCrSetDefaults() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async ({ id, payload }) =>
            (await api.post(`ContractReview/${id}/setDefaults`, payload)).data,
        onSuccess: (_d, vars) => {
            qc.invalidateQueries({ queryKey: crKeys.detail(vars.id) });
        },
    });
}

export function useCrSearch() {
    return useMutation({
        mutationFn: async (filter) => (await api.post("ContractReview/search", filter)).data,
    });
}

export function useCrAddNote() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async ({ id, note }) =>
            (await api.post(`ContractReview/${id}/notes`, { note })).data,
        onSuccess: (_d, vars) => qc.invalidateQueries({ queryKey: crKeys.detail(vars.id) }),
    });
}

export function useCrUploadAttachment() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async ({ id, file }) => {
            const fd = new FormData();
            fd.append("file", file);
            // IMPORTANT: do NOT hard-set 'Content-Type': 'multipart/form-data' here.
            // Doing so omits the auto-generated `; boundary=...` parameter which the
            // browser appends only when axios is allowed to populate the header itself.
            // Without the boundary, ASP.NET's multipart reader rejects the body and
            // IFormFile arrives null — which is exactly why uploads were failing.
            // We also override the JSON Content-Type default on the axios instance.
            return (await api.post(`ContractReview/${id}/attachments`, fd, {
                headers: { "Content-Type": undefined },
                // Server caps at 30 MB; axios should not silently truncate beyond that.
                maxBodyLength: 50 * 1024 * 1024,
                maxContentLength: 50 * 1024 * 1024,
            })).data;
        },
        onSuccess: (_d, vars) => qc.invalidateQueries({ queryKey: crKeys.detail(vars.id) }),
    });
}

export function useCrDeleteAttachment() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async ({ attId }) => (await api.delete(`ContractReview/attachments/${attId}`)).data,
        onSuccess: () => qc.invalidateQueries({ queryKey: crKeys.all }),
    });
}

// Epicor pre-fill (one-shot — not cached because user triggers it manually)
export async function fetchEpicorPrefill({ soNum, quoteNum }) {
    const params = new URLSearchParams();
    if (soNum) params.append("soNum", String(soNum));
    if (quoteNum) params.append("quoteNum", String(quoteNum));
    return (await api.get(`ContractReview/lookups/epicor?${params.toString()}`)).data;
}

// Print — returns the PDF blob URL for opening in a new tab.
export async function fetchCrPdf(id) {
    const res = await api.get(`ContractReview/${id}/print`, { responseType: "blob" });
    return URL.createObjectURL(res.data);
}

// Download — returns blob URL + filename.
export async function fetchAttachmentDownload(attId) {
    const res = await api.get(`ContractReview/attachments/${attId}/download`, { responseType: "blob" });
    // Try to extract filename from Content-Disposition
    let filename = "attachment";
    const cd = res.headers["content-disposition"];
    if (cd) {
        const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(cd);
        if (match && match[1]) filename = decodeURIComponent(match[1]);
    }
    return { url: URL.createObjectURL(res.data), filename };
}
